import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { useActiveLocation } from "@/contexts/ActiveLocationContext";
import { useAuth } from "@/hooks/useAuth";
import {
  useCreateStation,
  useDeleteStation,
  useStations,
  useUpdateStation,
} from "@/hooks/useStations";
import { getApiErrorMessage } from "@/lib/api/api";
import type { Station } from "@/types/station";

/**
 * Settings → Configuration → Stations (YC-36): the work areas of the active
 * location, such as Grill or Garde Manger. A recipe on the Prep page can be
 * assigned to one of them instead of to a single chef.
 *
 * Stations belong to a location, not to the organization, so this card follows
 * the location picked in the app shell. Writing needs `location:update`; anyone
 * else who reaches Configuration sees the list read-only.
 */
export function StationSettingsCard() {
  const { t } = useLingui();
  const { hasPermission } = useAuth();
  const { activeLocation } = useActiveLocation();
  const canManage = hasPermission("location:update");
  const locationId = activeLocation?.id;
  const locationName = activeLocation?.name;

  const { data: stations, isLoading, isError } = useStations(
    { locationId, includeInactive: true },
    locationId !== undefined,
  );
  const create = useCreateStation();
  const update = useUpdateStation();
  const remove = useDeleteStation();
  const [name, setName] = useState("");
  const [toDelete, setToDelete] = useState<Station | null>(null);
  const deleteName = toDelete?.name ?? "";

  const title = t`Stations`;
  const description = t`The work areas of a location, such as Grill or Garde Manger. A recipe on the Prep page can be assigned to a station instead of a chef.`;

  if (locationId === undefined) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{t`Choose a location to see its stations.`}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (isError || !stations) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{t`The stations could not be loaded.`}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const add = () => {
    const text = name.trim();
    if (text === "") return;
    create.mutate(
      { locationId, name: text },
      {
        onSuccess: () => {
          setName("");
          toast.success(t`Station added`);
        },
        // The server says when the name is taken, so its message is the useful one.
        onError: (err) => toast.error(getApiErrorMessage(err, t`Could not add the station`)),
      },
    );
  };

  const toggle = (station: Station, isActive: boolean) =>
    update.mutate(
      { id: station.id, data: { isActive } },
      { onError: (err) => toast.error(getApiErrorMessage(err, t`Could not update the station`)) },
    );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {locationName && (
          <p className="text-sm text-(--color-text-secondary)">{t`Stations of ${locationName}`}</p>
        )}
        {stations.length === 0 ? (
          <p className="text-sm text-(--color-text-secondary)">
            {t`No stations yet, so recipes can only be assigned to a chef.`}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {stations.map((station) => {
              const stationName = station.name;
              return (
              <li
                key={station.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-(--color-border) bg-(--color-surface-muted) px-3 py-2"
              >
                <span
                  className={
                    station.isActive
                      ? "text-sm font-medium text-(--color-text-primary)"
                      : "text-sm text-(--color-text-secondary)"
                  }
                >
                  {station.name}
                  {!station.isActive && <span className="ml-2 text-xs">{t`Inactive`}</span>}
                </span>
                {canManage && (
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={station.isActive}
                      aria-label={t`Active: ${stationName}`}
                      disabled={update.isPending}
                      onCheckedChange={(v) => toggle(station, v)}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t`Delete ${stationName}`}
                      onClick={() => setToDelete(station)}
                      className="size-8 text-(--color-text-secondary) hover:text-(--color-danger-text)"
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                )}
              </li>
              );
            })}
          </ul>
        )}

        {canManage && (
          <div className="flex items-center gap-2">
            <Input
              aria-label={t`Add a station`}
              value={name}
              maxLength={100}
              disabled={create.isPending}
              placeholder={t`e.g. Grill`}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
              className="max-w-xs"
            />
            <Button onClick={add} disabled={name.trim() === "" || create.isPending} className="gap-1.5">
              {create.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
              {t`Add`}
            </Button>
          </div>
        )}
      </CardContent>

      <ConfirmDeleteDialog
        open={toDelete !== null}
        onOpenChange={(open) => { if (!open) setToDelete(null); }}
        title={t`Delete station`}
        description={t`Delete "${deleteName}"? Recipes assigned to it keep going, with no station. To stop offering it but keep the history, turn it off instead.`}
        isPending={remove.isPending}
        onConfirm={() => {
          if (!toDelete) return;
          remove.mutate(toDelete.id, {
            onSuccess: () => {
              setToDelete(null);
              toast.success(t`Station deleted`);
            },
            onError: (err) => toast.error(getApiErrorMessage(err, t`Could not delete the station`)),
          });
        }}
      />
    </Card>
  );
}
